import {ComponentNext, Link}    from "@Common/react/cst2";
import {unique}                 from "@Common/utils/collections";
import {chain}                  from "@Common/utils/fs";

import {VariationSelectionState} from "./variations-state";
import {VariationsStock}         from "./variations-stock";

export type MasterPriceTier = {
    min: number,
    max: number,
    price: number,
    isFlatRate: boolean,
};
export type RangePriceTier = {
    isFlatRate: boolean,
    min: number,
    max: number,
    priceMin: number,
    priceMax: number,
};
export type VariationPriceTier = {
    isFlatRate: boolean,
    minQty: number,
    maxQty: number,
    price: number,
};

export const PricingRanges = ({next, paperPrices, quantity2, variationSelectionState, variationsStock}:{
    next:Link<any, {
        tiers: RangePriceTier[],
        matchedTier: RangePriceTier,
    }>,
    paperPrices: MasterPriceTier[],
    quantity2: any,
    variationSelectionState: ComponentNext<typeof VariationSelectionState>,
    variationsStock: ComponentNext<typeof VariationsStock>,
}) => next(
    !variationSelectionState.enabled
    ? null
    : chain(
        [
            ...(
                paperPrices && variationSelectionState.selection.matchingVariations.some(
                    variation => !variation.prices || variation.prices.length === 0
                )
                ? paperPrices.map(masterPriceAsRangeTier)
                : []
            ),
            ...(
                variationSelectionState.selection.matchingVariations
                .flatMap(variation => variation.prices ?? [])
                .map(variationPriceAsRangeTier)
            )
        ],
        // i think i should've treated the intervals as discrete instead of continuous :C
        (tiers) => mergeIntervalItems(
            tiers,
            (tier) => ({
                from: {value: tier.min, inclusive: true},
                to: {value: tier.max, inclusive: true},
            }),
            ({from, to}, tiers) => chain(
                (from.value + +(!from.inclusive)) === (to.value - +(!to.inclusive)),
                (mergedIsFlatRate) => ({
                    isFlatRate: mergedIsFlatRate,
                    min: from.value + +(!from.inclusive),
                    max: to.value - +(!to.inclusive),
                    priceMin: Math.min(...tiers.map(({isFlatRate, priceMin}) => (
                        mergedIsFlatRate === isFlatRate
                        ? priceMin
                        : priceMin * from.value
                    ))),
                    priceMax: Math.max(...tiers.map(({isFlatRate, priceMax}) => (
                        mergedIsFlatRate === isFlatRate
                        ? priceMax
                        : priceMax * from.value
                    ))),
                })
            ),
        ).filter(tier => tier.max >= tier.min).reduce(reduceRangeTierListExtension, []),
        (tiers) => (
            !variationsStock.show || variationsStock.allowBackOrder || variationsStock.availableQuantity <= 0
            ? tiers
            : (
                tiers
                .filter(tier => tier.min < variationsStock.availableQuantity)
                .map(tier => tier.max < variationsStock.availableQuantity ? tier : {
                    ...tier,
                    max: variationsStock.availableQuantity,
                })
            )
        ),
        (tiers) => ({
            tiers,
            matchedTier: !quantity2?.totalQuantity ? null : tiers.find(tier => (
                tier.min <= (quantity2?.totalQuantity ?? 1) && tier.max >= (quantity2?.totalQuantity ?? 1)
            )),
        }),
    )
);

const masterPriceAsRangeTier = ({isFlatRate, min, max, price}: MasterPriceTier): RangePriceTier => ({
    isFlatRate,
    min,
    max,
    priceMin: price,
    priceMax: price,
});

const variationPriceAsRangeTier = ({
    isFlatRate,
    minQty: min,
    maxQty: max,
    price,
}: VariationPriceTier): RangePriceTier => ({
    isFlatRate,
    min,
    max,
    priceMin: price,
    priceMax: price,
});

export const masterPriceAsVariationPrice = (masterPrice: MasterPriceTier): VariationPriceTier => ({
    isFlatRate: masterPrice.isFlatRate,
    minQty: masterPrice.min,
    maxQty: masterPrice.max,
    price: masterPrice.price,
});

type Point = {value: number, inclusive: boolean};
type Interval = {
    from: Point,
    to: Point
};

const mergeIntervalItems = <T>(
    items:T[],
    itemAsInterval: (item:T) => Interval,
    makeItemFromIntervalMerge: (interval: Interval, mergedItems: T[]) => T,
): T[] =>
{
    const itemsIntervals       = items.map((item) => ({item, interval: itemAsInterval(item)}));
    const pointValues          = unique(
        itemsIntervals
        .flatMap(({interval: {from, to}}) => [from.value, to.value])
        .sort((a, b) => a - b)
    );
    const mergedPointIntervals = pointValues.reduce<{item: T, interval: Interval}[]>((mergedIntervals, pointValue) =>
    {
        const openingItemPoints    = (
            itemsIntervals
            .filter(({interval: {from}}) => from.value === pointValue)
            .map(({item, interval: {from}}) => ({item, point: from}))
        );
        const closingItemPoints    = (
            itemsIntervals
            .filter(({interval: {to}}) => to.value === pointValue)
            .map(({item, interval: {to}}) => ({item, point: to}))
        );

        const openingPrecisionBoth = openingItemPoints.some(
            ({point}) => point.inclusive !== openingItemPoints[0].point.inclusive
        )
        const closingPrecisionBoth = closingItemPoints.some(
            ({point}) => point.inclusive !== closingItemPoints[0].point.inclusive
        );
        const strictOpenAndClose   = (
            openingItemPoints.some(({point}) => point.inclusive)
            && closingItemPoints.some(({point}) => point.inclusive)
        );

        const pointIntervalNeeded  = strictOpenAndClose || openingPrecisionBoth || closingPrecisionBoth;
        const interval             = {
            from: {inclusive: true, value: pointValue},
            to: {inclusive: true, value: pointValue},
        };
        return !pointIntervalNeeded ? mergedIntervals : [
            ...mergedIntervals,
            {
                interval,
                item: makeItemFromIntervalMerge(interval, unique([
                    ...openingItemPoints.filter(({point}) => point.inclusive),
                    ...closingItemPoints.filter(({point}) => point.inclusive),
                    ...itemsIntervals.filter(
                        ({interval: {from, to}}) => from.value < pointValue && to.value > pointValue
                    )
                ].map(({item}) => item))),
            }
        ];
    }, []);

    const pointValuePairs = (
        Array(Math.max(0, pointValues.length - 1))
        .fill(0)
        .map((_, index) => pointValues.slice(index, index+2) as [number, number])
    );
    const mergedIntervals = pointValuePairs.reduce<{item: T, interval: Interval}[]>((mergedIntervals, [from, to]) =>
    {
        const hitItemIntervals = itemsIntervals.filter(
            ({interval}) => Math.max(from, interval.from.value) < Math.min(to, interval.to.value)
        );
        const interval         = {
            from: {
                value: from,
                inclusive: (
                    hitItemIntervals.some(({interval}) => interval.from.inclusive && interval.from.value === from)
                    && !mergedPointIntervals.some(({interval}) => interval.from.value === from)
                )
            },
            to: {
                value: to,
                inclusive: (
                    hitItemIntervals.some(({interval}) => interval.to.inclusive && interval.to.value === to)
                    && !mergedPointIntervals.some(({interval}) => interval.to.value === to)
                )
            }
        };

        return hitItemIntervals.length === 0 ? mergedIntervals : [
            ...mergedIntervals,
            {
                interval,
                item: makeItemFromIntervalMerge(interval, hitItemIntervals.map(({item}) => item)),
            },
        ];
    }, []);

  return (
        [...mergedPointIntervals, ...mergedIntervals]
        .sort(({interval: {from: a}}, {interval: {from: b}}) => (+b.inclusive) - (+a.inclusive) + a.value - b.value)
        .map(({item}) => item)
    );
};

const reduceRangeTierListExtension = (tiers:RangePriceTier[], tier:RangePriceTier) =>
{
    if (tiers.length === 0) return [tier];

    const lastTier          = tiers.slice().pop();
    const tierFollows       = lastTier.max + 1 === tier.min;
    const ratesAreNonFlat   = !lastTier.isFlatRate && !tier.isFlatRate;
    const ratesAreIdentical = (
        ratesAreNonFlat
        && lastTier.priceMin === tier.priceMin
        && lastTier.priceMax === tier.priceMax
    );
    if (tierFollows && ratesAreIdentical) {
        return tiers.slice(0, -1).concat({
            ...lastTier,
            max: tier.max,
        });
    } else {
        return tiers.concat(tier);
    }
};

